import { SEO } from "@/components/SEO";
import { ArrowLeft } from "lucide-react";
import { Link, useLocation } from "wouter";

export default function TermsOfUse() {
  const [, setLocation] = useLocation();

  return (
    <>
      <SEO
        title="Terms of Use | Ganji Drinks - India's Best Ready-to-Drink Ragi Beverage Brand"
        description="Terms of Use for Ganji Drinks - Premium traditional ragi drinks and healthy millet beverages. Shop India's favorite ready-to-drink ragi malt beverages online with secure ordering."
        keywords="ragi drinks terms, healthy ragi beverages terms, traditional Indian drinks terms, millet drinks online terms, Ganji Drinks purchase terms, ragi malt beverages policy, organic ragi drinks terms, ready-to-drink ragi beverages"
      />

      <div
        className="min-h-screen"
        style={{ backgroundColor: "hsl(var(--ganji-warm-cream))" }}
      >
        {/* Empty Header / Spacer (Logo is global) */}
        <nav className="p-4 md:p-6 h-20 pointer-events-none sticky top-0 z-40 bg-transparent"></nav>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Back Button */}
          <div className="mb-8">
            <Link href="/">
              <a className="inline-flex items-center gap-2 hover:opacity-75 transition-opacity"
                style={{ color: 'hsl(var(--ganji-rustic-brown))' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5" />
                  <path d="M12 19l-7-7 7-7" />
                </svg>
                <span className="text-lg" style={{ fontFamily: "'Patrick Hand', cursive" }}>Back</span>
              </a>
            </Link>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h1
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{
                color: "hsl(var(--ganji-rustic-brown))",
                fontFamily: "'Permanent Marker', cursive"
              }}
            >
              Terms of Use
            </h1>

            <p
              className="text-lg mb-8"
              style={{ color: "hsl(var(--ganji-rustic-brown))" }}
            >
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <div className="space-y-8">
              <section>
                <h2
                  className="text-2xl font-bold mb-4"
                  style={{
                    color: "hsl(var(--ganji-rustic-brown))",
                    fontFamily: "'Permanent Marker', cursive"
                  }}
                >
                  Acceptance of Terms
                </h2>
                <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                  By accessing and using Ganji Drinks website and purchasing our products, you accept and agree to be bound
                  by these Terms of Use. If you do not agree to these terms, please do not use our website or services.
                </p>
              </section>

              <section>
                <h2
                  className="text-2xl font-bold mb-4"
                  style={{
                    color: "hsl(var(--ganji-rustic-brown))",
                    fontFamily: "'Permanent Marker', cursive"
                  }}
                >
                  About Our Products
                </h2>
                <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                  Ganji Drinks offers traditional Indian ragi-based beverages made from natural ingredients including
                  finger millet (ragi), jaggery, cardamom, and salt. Our products are prepared following traditional recipes
                  and hygiene standards.
                </p>
              </section>

              <section>
                <h2
                  className="text-2xl font-bold mb-4"
                  style={{
                    color: "hsl(var(--ganji-rustic-brown))",
                    fontFamily: "'Permanent Marker', cursive"
                  }}
                >
                  Orders and Payments
                </h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2" style={{ color: "hsl(var(--ganji-rustic-brown))" }}>Order Placement:</h3>
                    <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                      Orders can be placed through our website or via WhatsApp. All orders are subject to product availability.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2" style={{ color: "hsl(var(--ganji-rustic-brown))" }}>Pricing:</h3>
                    <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                      Prices are listed in Indian Rupees (INR) and are inclusive of applicable taxes. We reserve the right to
                      modify prices without prior notice.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2" style={{ color: "hsl(var(--ganji-rustic-brown))" }}>Payment:</h3>
                    <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                      Payments are processed securely through third-party payment providers. We do not store your payment
                      information on our servers.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2
                  className="text-2xl font-bold mb-4"
                  style={{
                    color: "hsl(var(--ganji-rustic-brown))",
                    fontFamily: "'Permanent Marker', cursive"
                  }}
                >
                  Delivery and Refunds
                </h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2" style={{ color: "hsl(var(--ganji-rustic-brown))" }}>Delivery:</h3>
                    <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                      We deliver within Bangalore city limits. Delivery times may vary based on location and order volume.
                      Freshness is guaranteed for delivered products.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2" style={{ color: "hsl(var(--ganji-rustic-brown))" }}>Refunds:</h3>
                    <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                      Refunds are only provided in case of product defects or delivery errors. Please contact us within
                      24 hours of delivery if you have any issues with your order.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2
                  className="text-2xl font-bold mb-4"
                  style={{
                    color: "hsl(var(--ganji-rustic-brown))",
                    fontFamily: "'Permanent Marker', cursive"
                  }}
                >
                  Health and Allergy Information
                </h2>
                <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                  Our Ganji drinks contain ragi (finger millet), jaggery, cardamom, and salt. If you have any allergies
                  or dietary restrictions, please review the ingredients carefully. Consult with a healthcare professional
                  if you have specific health concerns.
                </p>
              </section>

              <section>
                <h2
                  className="text-2xl font-bold mb-4"
                  style={{
                    color: "hsl(var(--ganji-rustic-brown))",
                    fontFamily: "'Permanent Marker', cursive"
                  }}
                >
                  Intellectual Property
                </h2>
                <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                  All content on this website, including text, images, logos, and designs, is the property of Ganji Drinks
                  and is protected by copyright and other intellectual property laws. You may not use our content without
                  explicit permission.
                </p>
              </section>

              <section>
                <h2
                  className="text-2xl font-bold mb-4"
                  style={{
                    color: "hsl(var(--ganji-rustic-brown))",
                    fontFamily: "'Permanent Marker', cursive"
                  }}
                >
                  Limitation of Liability
                </h2>
                <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                  Ganji Drinks shall not be liable for any indirect, incidental, or consequential damages arising from
                  the use of our products or website. Our liability is limited to the purchase price of the product.
                </p>
              </section>

              <section>
                <h2
                  className="text-2xl font-bold mb-4"
                  style={{
                    color: "hsl(var(--ganji-rustic-brown))",
                    fontFamily: "'Permanent Marker', cursive"
                  }}
                >
                  Changes to Terms
                </h2>
                <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                  We reserve the right to modify these Terms of Use at any time. Changes will be effective immediately
                  upon posting on our website. Your continued use of our services constitutes acceptance of any changes.
                </p>
              </section>

              <section>
                <h2
                  className="text-2xl font-bold mb-4"
                  style={{
                    color: "hsl(var(--ganji-rustic-brown))",
                    fontFamily: "'Permanent Marker', cursive"
                  }}
                >
                  Contact Information
                </h2>
                <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                  If you have any questions about these Terms of Use, please contact us:
                </p>
                <div className="mt-4 space-y-2" style={{ color: "hsl(var(--ganji-rustic-brown))" }}>
                  <p>Email: ganjidrinks@gmail.com</p>
                  <p>Phone: +91 9019323880</p>
                  <p>Address: Bangalore, India</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
